import cx from "classnames";
import { useCallback, useRef } from "react";
import { Link } from "react-router-dom";

import DeltaCounts from "components/DeltaCounts";
import FlashContext from "components/FlashMessages/FlashContext";
import ListEntitiesItem from "components/ListEntitiesItem";
import { prepareName } from "components/state/State";
import VendorVersion from "components/VendorVersion";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Toggle from "ds/components/Toggle";
import useTypedContext from "hooks/useTypedContext";
import useReviewRun from "shared/Run/useReviewRun";
import { isRunReviewable } from "shared/Run/useReviewRun/accessValidation";
import { Run, RunReviewDecision, Stack, StackConfigVendor } from "types/generated";
import { getRuntimeConfigVendorInfo, getVendorName } from "utils/vendor";
import RunStateBadge from "components/Run/elements/StateBadge";
import { StackContext } from "views/Stack/Context";
import Tooltip from "ds/components/Tooltip";

import { COLUMN_GAP, COLUMN_ORDER_APPROVAL, COLUMN_ORDER_USER } from "../constants";
import RunMetaInfoListItem from "../MetaInfoListItem";
import styles from "./styles.module.css";

type RunElementProps = {
  stackId: Stack["id"];
  run: Run;
  checked: boolean;
  onCheck: (item: Run, checked: boolean) => void;
  vendorConfig?: StackConfigVendor;
  hash: string;
  isAnsible?: boolean;
  selectable?: boolean;
};

const RunElement = (props: RunElementProps) => {
  const { stackId, vendorConfig, run, checked, onCheck, isAnsible, selectable } = props;

  const { reportSuccess } = useTypedContext(FlashContext);
  const { hasAtLeastWriteAccess } = useTypedContext(StackContext);

  const rowRef = useRef<HTMLDivElement>(null);

  const link = `/stack/${stackId}/run/${run.id}`;

  const vendorDetails =
    (vendorConfig &&
      run.runtimeConfig &&
      getRuntimeConfigVendorInfo(run.runtimeConfig)) ||
    undefined;

  const vendorIcons = vendorDetails && (
    <>
      <VendorVersion icon={vendorDetails.vendor.icon} text={vendorDetails.vendor.text} />
      {vendorDetails.vendorTool && (
        <VendorVersion icon={vendorDetails.vendorTool.icon} text={vendorDetails.vendorTool.text} />
      )}
    </>
  );

  const handleOnChange = useCallback(
    () => onCheck(run, !checked),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCheck, checked]
  );
  const [addRunReview, { loading: approveRunLoading }] = useReviewRun({
    refetchQueries: ["ListRuns"],
  });

  const onApproveRun = () =>
    addRunReview(
      { stack: stackId, run: run.id, decision: RunReviewDecision.Approve, note: "" },
      (data) => {
        if (data?.runReview.id) {
          reportSuccess({ message: "Run has been approved" });
        }
      }
    );
  const runNeedsApproval = isRunReviewable(run);

  return (
    <ListEntitiesItem
      ref={rowRef}
      direction="row"
      align="center"
      grid
      gridTemplate={selectable ? COLUMN_ORDER_APPROVAL : COLUMN_ORDER_USER}
      gap={COLUMN_GAP}
      className={cx(styles.item, {
        [styles.needsApproval]: runNeedsApproval,
      })}
      role="row"
    >
      {selectable && (
        <Box role="cell">
          <Toggle
            variant="checkbox"
            id={run.id}
            onChange={handleOnChange}
            isSelected={checked}
            aria-label={checked ? "Unselect this run" : "Select this run"}
          />
        </Box>
      )}
      <Box role="cell" direction="column">
        <Link className={styles.focusOutline} to={link}>
          <Box>
            <RunStateBadge state={run.state} />
          </Box>
        </Link>
      </Box>

      <Box role="rowheader" direction="column">
        <Link className={styles.focusOutline} to={link}>
          <RunMetaInfoListItem run={run} stackId={stackId} />
        </Link>
      </Box>

      <Box role="cell" direction="column">
        <Link
          to={link}
          className={cx(styles.link, styles.focusOutline)}
          aria-label={`Go to run. State ${prepareName(run.state)}. Vendor ${getVendorName(vendorConfig?.__typename)}.${vendorDetails?.vendor.text && ` Version ${vendorDetails?.vendor.text}.`}`}
        >
          {vendorIcons}
        </Link>
      </Box>

      <Box align="center" role="cell">
        {run.delta && (
          <DeltaCounts
            compact
            link={link}
            linkAriaLabel="Go to this run"
            className={styles.delta}
            delta={run.delta}
            isAnsible={isAnsible}
          />
        )}
      </Box>
      <Box role="cell" justify="end">
        {runNeedsApproval && (hasAtLeastWriteAccess.result || hasAtLeastWriteAccess.message) && (
          <Tooltip
            active={!!hasAtLeastWriteAccess.message}
            on={(props) => (
              <Button
                {...props}
                onPress={onApproveRun}
                variant="secondary"
                size="small"
                disabled={approveRunLoading || !hasAtLeastWriteAccess.result}
                loading={approveRunLoading}
              >
                Approve
              </Button>
            )}
          >
            {hasAtLeastWriteAccess.message}
          </Tooltip>
        )}
      </Box>
    </ListEntitiesItem>
  );
};

export default RunElement;
